/**
 * A labelled text input. Real <label for>, real <input> — a tap anywhere on the
 * label focuses the field, which matters when the target is a thumb.
 *
 * `inputMode` is exposed because it chooses the keyboard iOS raises: `tel` for
 * a phone, `email` for an address, `decimal` for a measurement. Getting that
 * wrong costs the surveyor a keyboard switch on every field.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'url';
  autoComplete?: string;
  /** Renders a textarea instead — section notes, ad hoc "something else". */
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  /** Monospace readout for values that are numbers or codes. */
  mono?: boolean;
  /** Stamped as `data-note-id` so the photo viewer's Note action can focus this field. */
  dataId?: string;
}

/**
 * The draft — D-037.
 *
 * Every answer persists before it is visible (SurveyProvider rule 1), so
 * `value` arrives one IndexedDB round-trip after each keystroke. A controlled
 * input whose prop lags is put back to the prop after every input event: on a
 * phone that eats fast keystrokes, and it rejects the keyboard's own
 * suggestions — autocorrect writes the word, React writes the old value back.
 * *"when my phone suggests a word and I tap it, the suggestion doesn't fill
 * the field."*
 *
 * So while the field has focus the DRAFT is what it shows, and the prop is
 * adopted only while it does not: an external change (a sync, a fix, another
 * device) still lands, and leaving the field adopts the stored value — which
 * is also where a formatter's echo (the phone number's brackets) now lands.
 */
function useDraft(value: string): {
  shown: string;
  onFocus: () => void;
  onBlur: () => void;
  edit: (next: string) => void;
} {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);
  return {
    shown: focused ? draft : value,
    onFocus: () => {
      setDraft(value);
      setFocused(true);
    },
    onBlur: () => setFocused(false),
    edit: setDraft,
  };
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  inputMode = 'text',
  autoComplete,
  multiline = false,
  rows = 3,
  disabled = false,
  mono = false,
  dataId,
}: TextFieldProps): ReactNode {
  const id = useId();
  const className = mono ? 'field__input field__input--mono' : 'field__input';
  const draft = useDraft(value);
  const handle = (next: string): void => {
    draft.edit(next);
    onChange(next);
  };

  return (
    <div className="field">
      <div className="field__head">
        <label className="field__label" data-legibility="question" htmlFor={id}>
          {label}
        </label>
      </div>
      {multiline ? (
        <textarea
          id={id}
          className={className}
          {...(dataId ? { 'data-note-id': dataId } : {})}
          value={draft.shown}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={draft.onFocus}
          onBlur={draft.onBlur}
          onChange={(event) => handle(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className={className}
          {...(dataId ? { 'data-note-id': dataId } : {})}
          type="text"
          value={draft.shown}
          inputMode={inputMode}
          placeholder={placeholder}
          disabled={disabled}
          {...(autoComplete ? { autoComplete } : {})}
          onFocus={draft.onFocus}
          onBlur={draft.onBlur}
          onChange={(event) => handle(event.target.value)}
        />
      )}
    </div>
  );
}
